"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useSession } from "@/lib/auth-client";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Logo } from "@/components/Logo";
import { RiskAgreementModal } from "@/components/RiskAgreementModal";
import { SiteFooter } from "@/components/SiteFooter";
import { ThemeToggle } from "@/components/ThemeToggle";
import type { LandingDict } from "@/content/landing";
import { t } from "@/content/ui";
import { localePath, LOCALE_TAG, type Locale } from "@/lib/i18n";
import { getPlanPrices } from "@/lib/api";
import { SymbolIcon, VenueMark } from "@/components/SymbolIcon";
import { AnnouncementCard } from "@/components/AnnouncementCard";
import { MarketsStrip } from "@/components/MarketsStrip";
import { IconChip, IconKey, IconStep, IconTimeframes, IconDips } from "@/components/HomeIcons";

/**
 * Public landing page, one per locale (app/page.tsx = en, app/ht/page.tsx = ht).
 *
 * ⚠️ THIS IS THE ONE PUBLIC PAGE THAT MUST STAY A CLIENT COMPONENT, and it is worth knowing why so
 * nobody "fixes" it: it reads the session to bounce a signed-in user to /dashboard, and it fetches
 * live plan prices. Both are runtime, per-visitor concerns.
 *
 * It IS crawlable, though, and that took one character of thought: see the `session` guard below.
 * Until 2026-08-15 this page served ~7 KB of "Loading…" to any client that did not run JavaScript,
 * because the guard also tested `isPending` — which is ALWAYS true during a server render.
 * Measured after the fix: 981 visible words in English, 1145 in Kreyòl.
 *
 * The DICTIONARY IS PASSED IN, already resolved, rather than imported here. Importing
 * content/landing.ts would bundle BOTH locales into every visitor's JavaScript to use one.
 */
export function Landing({
  locale,
  dict,
}: {
  locale: Locale;
  dict: LandingDict;
}) {
  const { data: session, isPending } = useSession();
  const router = useRouter();
  // BACK TO TOP. The menu scrolls away with the page, so a yellow round button at the bottom right
  // brings the reader back to it. It shows only once the reader is past the first screen, where
  // the menu is out of view; the listener is passive because it never cancels the scroll.
  const [showTop, setShowTop] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > window.innerHeight);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const [showAgreement, setShowAgreement] = useState(false);
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [spotPrices, setSpotPrices] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isPending && session) router.replace("/dashboard");
  }, [session, isPending, router]);

  useEffect(() => {
    // Live, admin-settable prices from the public /plans/prices endpoint (never hardcoded).
    getPlanPrices()
      .then(setPrices)
      .catch(() => {});
    getPlanPrices("spot")
      .then(setSpotPrices)
      .catch(() => {});
  }, []);

  const fmt = (v: string | undefined) => (v ? `$${(Number(v) / 1_000_000).toLocaleString()}` : "—");
  const usd = (id: string) => fmt(prices[id]);
  // THE NETWORKS A SUBSCRIPTION IS PAID ON (config/chains.ts + NEAR): drawn small under every
  // price as the coins' own marks — what the buyer will see in the wallet step.
  // The stablecoins first (the exact price), then the networks (their own coin at the oracle price).
  const payOn = (
    <div className="mt-3 flex items-center gap-1.5 text-[11px] text-muted">
      <span>{dict.pricing.payOn}</span>
      {["USDCUSDT", "USDTUSDT"].map((s) => (
        <SymbolIcon key={s} symbol={s} size={16} />
      ))}
      <span className="text-faint">·</span>
      {["POLUSDT", "ETHUSDT", "NEARUSDT"].map((s) => (
        <SymbolIcon key={s} symbol={s} size={16} />
      ))}
    </div>
  );

  // ⚠️ THE GUARD IS `session`, NOT `isPending || session`, and the difference is the whole page.
  // `useSession` is ALWAYS pending during the server render, so the old condition returned the
  // loading screen every time — the served HTML was "Loading…" and nothing else, for crawlers and
  // for every anonymous visitor until hydration. Waiting on `session` alone renders the real page
  // server-side and still bounces a signed-in user, who sees this for the moment the redirect
  // takes rather than seeing the marketing page flash.
  if (session)
    return (
      <main className="flex min-h-screen items-center justify-center text-muted">
        <p>{dict.loading}</p>
      </main>
    );

  const PLANS = [
    { id: "quarterly", months: 3 },
    { id: "semi_annual", months: 6, popular: true },
    { id: "annual", months: 12 },
  ];


  /**
   * ONE MARK PER CARD, and one per pipeline node, from `components/HomeIcons` — drawn as a set
   * with the instrument marks (owner 2026-08-19: "use better icon on home page like ML maybe a
   * brain"; 2026-09-25: "make them like the symbol one, pro and quality"). The first set was three
   * 1.6px line glyphs and, on the pipeline, a bare status dot inside each hex. `currentColor`
   * throughout, so they inherit the hex's yellow and follow the theme with everything else.
   */
  const ICONS = [
    <IconTimeframes key="tf" />,
    <IconKey key="key" />,
    <IconChip key="chip" />,
    <IconDips key="dips" />,
  ];

  return (
    <>
      <AnnouncementCard dict={dict.announce} />
      {/* `lang` on the content subtree, not on <html>: the root layout hardcodes lang="en" and
          cannot see the URL without making every route dynamic. Per spec `lang` applies to an
          element and its descendants. Same approach as PublicShell. */}
      {/* THE MENU SITS HALF ON A YELLOW BAND and scrolls away with the page; the back-to-top button
          at the bottom right brings it back. The band runs full width from the top edge down to
          the middle of the square: it is the header's upper half (`h-1/2`), and the header's
          padding is the same above and below the square, so that half ends on the square's
          centre line. The band sits in the header, a sibling of <main> rather than inside its
          max-w-6xl column, so it spans the viewport without a 100vw width, which would add a
          horizontal scroll on screens with a scrollbar. */}
      <header lang={LOCALE_TAG[locale]} className="relative">
        <div aria-hidden className="absolute inset-x-0 top-0 h-1/2 bg-yellow/50" />
        <div className="relative mx-auto max-w-6xl px-4 py-2 sm:py-3">
          {/* The square menu, centred on the page's column inside the yellow band. A squared bar
              (rounded-lg) with a 3px yellow stroke (2px below sm), and tighter padding below sm so a
              320px phone keeps the logo and the three controls on one row. */}
          <nav
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border-2 border-yellow bg-bg-1/70 px-3 py-2 backdrop-blur-sm sm:gap-3 sm:border-[3px] sm:px-5 sm:py-2.5"
          >
            <Logo />
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Link
                href={localePath(locale, "/tutorials")}
                className="btn-ghost hidden sm:inline-flex"
              >
                {t(locale, "nav.tutorials")}
              </Link>
              <Link
                href={localePath(locale, "/faq")}
                className="btn-ghost hidden sm:inline-flex"
              >
                {t(locale, "nav.faq")}
              </Link>
              <LanguageSwitcher locale={locale} path="/" />
              <ThemeToggle />
              {/* HIDDEN BELOW sm, and nothing is lost by it. Tutorials and FAQ already hide here, but
                  "Sign in" and "Get started" both stayed, so a phone still had the logo plus five
                  controls in one pill — the row could only resolve by wrapping, and the buttons wrapped
                  their own labels ("Sign"/"in"). The hero directly below carries BOTH actions as
                  full-width buttons ("Create account" / "I already have one"), so on a phone this is
                  the same destination twice, competing for the narrowest row on the page.
                  "Get started" stays: it is the primary conversion and it must be reachable before
                  the reader scrolls. */}
              <Link
                href={localePath(locale, "/login")}
                className="btn-ghost hidden sm:inline-flex"
              >
                {t(locale, "nav.signIn")}
              </Link>
              {/* min-h-11 BELOW sm only. `.btn` is px-4 py-2 text-sm ≈ 38px tall, and Apple's
                  minimum touch target is 44px — the figure current mobile-header guidance repeats
                  (headers 44–56px tall, targets never under 44). Raised on phones only, because
                  applying it globally would inflate every button in the dense admin tables where
                  the pointer is a mouse and 38px is correct. */}
              <button
                className="btn min-h-11 sm:min-h-0"
                onClick={() => setShowAgreement(true)}
              >
                {t(locale, "nav.getStarted")}
              </button>
            </div>
          </nav>
        </div>
      </header>
      {showTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label={t(locale, "nav.backToTop")}
          title={t(locale, "nav.backToTop")}
          className="fixed bottom-5 right-5 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-yellow text-black shadow-lg transition-transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M12 19V5M5 12l7-7 7 7" />
          </svg>
        </button>
      )}
      <main lang={LOCALE_TAG[locale]} className="mx-auto max-w-6xl px-4 pb-16">
        {/* Hero */}
        <section className="mt-12 grid items-start gap-10 lg:grid-cols-[1.3fr_1fr]">
          <div>
            <div className="label mb-3 text-yellow">{dict.hero.eyebrow}</div>
            {/* `md:leading-none` keeps the Tailwind 3 look: there `md:text-5xl` carried line-height 1 and
                overrode `leading-[1.1]`; Tailwind 4 lets an explicit leading win instead. */}
            <h1 className="text-4xl font-bold leading-[1.1] tracking-tight md:text-5xl md:leading-none">
              {dict.hero.titleBefore}
              <span className="text-yellow">{dict.hero.titleAccent}</span>
              {dict.hero.titleAfter}
            </h1>
            <p className="mt-5 max-w-xl text-muted">{dict.hero.body}</p>

            <div className="mt-5 flex flex-wrap items-center gap-2.5">
              <span className="label mb-0!">{dict.hero.worksWith}</span>
              <span className="chip mark-yellow font-mono text-xs">Bybit</span>
              <span className="chip mark-yellow font-mono text-xs">Binance</span>
              <span className="chip mark-yellow font-mono text-xs">MEXC</span>
              {/* cTrader beside the two crypto venues (owner 2026-10-02: "add ctrader next to binance and
                  bybit too"). */}
              <span className="chip mark-yellow font-mono text-xs">cTrader</span>
              <span className="text-xs text-muted">{dict.hero.venuesNote}</span>
            </div>

            <MarketsStrip label={dict.hero.marketsLabel} note={dict.hero.marketsNote} />

            <div className="mt-7 flex flex-wrap gap-3">
              <button className="btn" onClick={() => setShowAgreement(true)}>
                {dict.hero.ctaCreate}
              </button>
              <Link href={localePath(locale, "/login")} className="btn-secondary">
                {dict.hero.ctaHave}
              </Link>
            </div>

            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted">
              {dict.hero.badges.map((b, i) => (
                <span key={b} className="flex items-center gap-1.5">
                  <span
                    className={`status-dot h-1.5 w-1.5 ${i === 1 ? "status-dot-yellow" : ""}`}
                  />{" "}
                  {b}
                </span>
              ))}
            </div>

          </div>

          {/* Tiger mark + how-it-works panel */}
          <div className="flex flex-col items-center gap-7">
            <span className="tiger-mark w-60 max-w-full md:w-72">
              {/* eslint-disable-next-line @next/next/no-img-element -- static brand SVG */}
              <img
                src="/tiger.svg"
                alt="Twoke.IO"
                className="w-full"
                style={{ filter: "drop-shadow(0 0 45px rgb(250 204 21 / 0.28))" }}
              />
            </span>
            {/* `#how-it-works` lives here (the footer's "How it works" link): the separate
                "Signal → validation → execution" panel that held it was removed (owner
                2026-10-03: the page explained the flow twice — "remove one"). This card is the
                one kept: it is in the hero and it has all four steps, sizing included. */}
            <div className="panel bracketed w-full scroll-mt-24" id="how-it-works">
              <div className="label mb-3 text-yellow">{dict.pipeline.label}</div>
              <ol className="space-y-3 text-sm">
                {dict.pipeline.steps.map((s, i) => (
                  <li key={s.t} className="flex gap-3">
                    <span className="mt-px shrink-0">
                      <IconStep n={i + 1} />
                    </span>
                    <span>
                      <span className="font-medium">{s.t}</span>
                      <span className="block text-xs text-muted">{s.s}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        {/* What is copy trading */}
        <section className="mt-20 grid gap-8 lg:grid-cols-[1fr_1.1fr]">
          <div>
            <div className="label mb-3 text-yellow">{dict.what.eyebrow}</div>
            <h2 className="text-2xl font-semibold tracking-tight">
              {dict.what.h2}
            </h2>
            <p className="mt-4 text-muted">{dict.what.p1}</p>
            <p className="mt-3 text-muted">{dict.what.p2}</p>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2">
            {dict.what.cards.map((c) => (
              <li key={c.t} className="panel">
                <div className="flex items-center gap-2">
                  <span className="status-dot-yellow status-dot h-2 w-2" />
                  <span className="font-medium">{c.t}</span>
                </div>
                <p className="mt-1.5 text-sm text-muted">{c.d}</p>
              </li>
            ))}
          </ul>
        </section>

        {/* Feature grid — a row of four since Spot (2026-10-11) */}
        <section className="mt-20 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {dict.features.map((f, i) => (
            <div key={f.t} className="panel">
              {ICONS[i]}
              <h3 className="mt-4 font-medium">{f.t}</h3>
              <p className="mt-1 text-sm text-muted">{f.d}</p>
            </div>
          ))}
        </section>

        {/* Pricing */}
        <section className="mt-20 scroll-mt-24" id="pricing">
          <div className="label mb-2 text-center text-yellow">
            {dict.pricing.eyebrow}
          </div>
          <h2 className="text-center text-2xl font-semibold tracking-tight">
            {dict.pricing.h2}
          </h2>
          <p className="mx-auto mt-2 max-w-lg text-center text-sm text-muted">
            {dict.pricing.sub}
          </p>
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {PLANS.map((p) => (
              <div
                key={p.id}
                className={`relative flex flex-col ${p.popular ? "panel-accent" : "panel"}`}
              >
                {p.popular && (
                  <span
                    className="badge absolute -top-2.5 right-4 text-[#0a0a0a]"
                    style={{ background: "rgb(var(--yellow))" }}
                  >
                    {dict.pricing.popular}
                  </span>
                )}
                <div className="label text-yellow">{dict.pricing.planLabels[p.id]}</div>
                <div className="num mt-2 text-4xl font-bold tracking-tight">
                  {usd(p.id)}
                </div>
                <div className="text-xs text-muted">
                  {p.months} {dict.pricing.months} · {dict.pricing.perMaster}
                </div>
                {payOn}
                <ul className="mt-4 flex-1 space-y-1.5 text-sm text-muted">
                  {dict.pricing.features.map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <span className="text-yellow">✓</span> {f}
                    </li>
                  ))}
                </ul>
                <button
                  className="btn mt-5 w-full"
                  onClick={() => setShowAgreement(true)}
                >
                  {dict.pricing.cta}
                </button>
              </div>
            ))}
            {/* SPOT (2026-10-11): the fourth card — sold per year, its own price (Subscription v3),
                the same networks. One subscription runs Spot on one account of each exchange
                (migration 0102), shown as "Pay 1, get 3" with the three exchanges' marks. */}
            <div className="panel relative flex flex-col">
              <div className="label text-yellow">{dict.pricing.spotLabel}</div>
              <div className="num mt-2 text-4xl font-bold tracking-tight">{fmt(spotPrices.annual)}</div>
              <div className="text-xs text-muted">
                12 {dict.pricing.months} · {dict.pricing.perAccount}
              </div>
              <div className="mt-3 inline-flex w-fit items-center gap-2 rounded-full border border-yellow/50 bg-yellow/10 px-3 py-1 text-sm font-semibold text-yellow">
                {dict.pricing.spotBundle}
                <span className="flex items-center gap-1">
                  {["bybit", "binance", "mexc"].map((v) => (
                    <VenueMark key={v} venue={v} size={18} />
                  ))}
                </span>
              </div>
              {payOn}
              <ul className="mt-4 flex-1 space-y-1.5 text-sm text-muted">
                {dict.pricing.spotFeatures.map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <span className="text-yellow">✓</span> {f}
                  </li>
                ))}
              </ul>
              <button className="btn mt-5 w-full" onClick={() => setShowAgreement(true)}>
                {dict.pricing.cta}
              </button>
            </div>
          </div>
          <p className="mt-4 text-center text-[11px] text-muted">
            {dict.pricing.footnote}
          </p>
        </section>

        {/* Trading-risk agreement */}
        <section className="mt-20">
          <div
            className="panel bracketed border-warn/30"
            style={{
              background:
                "linear-gradient(180deg, rgb(var(--warn) / 0.05), rgb(var(--bg-1)))",
            }}
          >
            <div className="flex items-center gap-2">
              <span className="status-dot-warn status-dot" />
              <h2 className="font-semibold text-warn">{dict.risk.h2}</h2>
            </div>
            <ul className="mt-4 grid gap-3 text-sm text-muted md:grid-cols-2">
              {dict.risk.items.map((r) => (
                <li key={r.b}>
                  <b className="text-primary">{r.b}</b> {r.t}
                </li>
              ))}
            </ul>
            <div className="mt-5">
              <button className="btn" onClick={() => setShowAgreement(true)}>
                {dict.risk.cta}
              </button>
            </div>
            <p className="mt-3 text-[11px] text-muted">{dict.risk.note}</p>
          </div>
        </section>
      </main>

      {/* Sibling of <main>, not a child — the band must span the viewport. See PublicShell. */}
      <SiteFooter locale={locale} />

      {/* The landing page shows the disclosure early as a courtesy, but /signup gates itself
          (app/signup/page.tsx) so no entry path can skip it. Agreeing here marks it satisfied for
          this navigation so the user is not shown the identical modal twice in a row. */}
      {showAgreement && (
        <RiskAgreementModal
          locale={locale}
          onAgree={() =>
            router.push(`${localePath(locale, "/signup")}?disclosed=1`)
          }
          onClose={() => setShowAgreement(false)}
        />
      )}
    </>
  );
}
